import { DrawerBodyWrapper, DrawerFooterWrapper, TOAST_TYPE } from '@base'
import { Button, Input, Select } from '@baseV2'
import styled from 'styled-components'
import { ChargeListWrapper } from '@pages/Admin/Orders/ManageOrder/EditInvoice'
import { strings } from '@constants'
import { useEffect, useState } from 'react'
import { ListItemWrapper, ListWrapper } from '../PaymentTerms'
import { Delete } from '@material-ui/icons'
import { useDispatch } from 'react-redux'
import { showLoader, showToast } from '@data/state/action'
import { getProductByCategory } from '@data'
import { displayAmount } from '@utils'

const AddButtonWrapper = styled.div`
	display: flex;
	justify-content: flex-end;
`

// v2 Input hands back the change event; v1 returned the absolute value for number inputs
const toAbsNumber = (e) => (e.target.value === '' ? '' : Math.abs(e.target.value))

// every other charge type is single-use; only these two make sense more than once on a PO
const REPEATABLE_CHARGES = ['supply component', 'service component']
const isRepeatableCharge = (product) => REPEATABLE_CHARGES.includes(product?.product_name?.trim()?.toLowerCase())

const deriveFormState = (data) => {
	let _state = {
		product_id: {
			id: 'product-id',
			label: 'Product',
			placeholder: 'Choose Product',
			value: '',
			dirty: false,
			required: false,
		},
		hsn_code: {
			id: 'hsn-code',
			label: 'HSN Code',
			placeholder: 'Enter HSN Code',
			value: '',
			dirty: false,
			required: false,
		},
		rate_per_mt: {
			id: 'rate-per-mt',
			label: 'Rate per MT',
			placehodler: 'Enter Rate per MT',
			value: '',
			dirty: false,
			required: false,
		},
		rate_lumpsum: {
			id: 'rate-lumpsum',
			label: 'Lumpsum Amount',
			placeholder: 'Enter Lumpsum Amount',
			value: '',
			dirty: false,
			required: false,
		},
	}
	if (data) {
		_state.product_id.value = data?.product_id
		_state.hsn_code = data?.hsn_code
		_state.rate_per_mt = data?.rate_per_mt
		_state.rate_lumpsum = data?.rate_lumpsum
	}
	return _state
}

const AddOtherCharges = ({ update, edit = false, submitCallback, editCallback, data = null }) => {
	const dispatch = useDispatch()
	const [otherChargesList, setOtherChargesList] = useState(data ?? [])
	const [OtherChargesTypeList, setOtherChargesTypeList] = useState([])
	const [formState, setFormState] = useState(deriveFormState())

	useEffect(() => {
		dispatch(showLoader(true))
		getProductByCategory('other_services')
			.then((response) => {
				setOtherChargesTypeList(response)
			})
			.catch((error) => {})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}, [])

	const updateState = (key) => (value) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				value,
			},
		}))
	}

	const selectOtherChargesType = (value) => {
		updateState('product_id')(value?.product_id)
		updateState('hsn_code')(value?.hsn_code)
	}

	const addChargeHandler = () => {
		let _temp = OtherChargesTypeList.find((item) => item.product_id === formState?.product_id?.value)
		if (!_temp) return

		let _prevProducts = otherChargesList?.filter((item) => item.product_id === _temp.product_id)
		if (_prevProducts?.length && !isRepeatableCharge(_temp)) {
			dispatch(showToast(true, strings('msg_charge_already_added', [_temp?.product_name]), { type: TOAST_TYPE.ERROR }))
			return
		}

		let _itemId = `${_temp.product_id}_${(_prevProducts?.length ?? 0) + 1}`

		setOtherChargesList([
			...otherChargesList,
			{
				product_id: _temp?.product_id,
				rate_per_mt: formState?.rate_per_mt?.value !== '' ? formState?.rate_per_mt?.value : null,
				hsn_code: _temp.hsn_code,
				product_name: _temp?.product_name,
				rate_lumpsum: formState?.rate_lumpsum?.value !== '' ? formState?.rate_lumpsum?.value : null,
				category_id: 'other_services',
				category_name: 'Other Services',
				item_id: _itemId,
			},
		])
		clearState()
	}

	const deleteOtherCharge = (_val, index) => {
		setOtherChargesList(otherChargesList.filter((item, idx) => idx !== index))
	}

	const clearState = () => {
		setFormState(deriveFormState())
	}

	const submitHandler = () => {
		submitCallback(otherChargesList)
	}

	const updateHandler = () => {
		editCallback(otherChargesList)
	}

	return (
		<>
			<DrawerBodyWrapper>
				<ChargeListWrapper>
					<Select
						label={strings(['other', 'charges', 'type'])}
						placeholder={strings(['select', 'other', 'charges', 'type'])}
						primaryKey='product_id'
						displayKey='product_name'
						value={OtherChargesTypeList.find((item) => item.product_id === formState?.product_id?.value) ?? {}}
						data={OtherChargesTypeList}
						onChange={(value) => {
							selectOtherChargesType(value)
						}}
					/>
					<Input
						id={formState?.hsn_code?.id}
						label={formState?.hsn_code?.label}
						placeholder={formState?.hsn_code?.placeholder}
						value={formState?.hsn_code?.value ?? ''}
						onChange={(e) => {
							updateState('hsn_code')(e.target.value)
						}}
						disabled
					/>
				</ChargeListWrapper>
				<ChargeListWrapper>
					<Input
						type='number'
						label={formState?.rate_per_mt?.label}
						placeholder={formState?.rate_per_mt?.placehodler}
						value={formState?.rate_per_mt?.value}
						onChange={(e) => {
							updateState('rate_per_mt')(toAbsNumber(e))
						}}
						disabled={!!formState?.rate_lumpsum?.value}
					/>
					<Input
						type='number'
						label={formState?.rate_lumpsum?.label}
						placeholder={formState?.rate_lumpsum?.placeholder}
						value={formState?.rate_lumpsum?.value ?? ''}
						onChange={(e) => {
							updateState('rate_lumpsum')(toAbsNumber(e))
						}}
						disabled={!!formState?.rate_per_mt?.value}
					/>
				</ChargeListWrapper>
				<AddButtonWrapper>
					<Button size='base' onClick={addChargeHandler} disabled={!formState?.product_id?.value}>
						{strings('add')}
					</Button>
				</AddButtonWrapper>

				<ListWrapper>
					{otherChargesList?.map((_item, index) => {
						let rate =
							_item?.rate_per_mt !== null
								? `${displayAmount(_item?.rate_per_mt)} per MT`
								: `${displayAmount(_item?.rate_lumpsum)} lumpsum`

						return (
							<ListItemWrapper oneButton>
								<div>{`${_item?.product_name} (${rate})`}</div>

								<Button size='base' variant='outline' color='danger' onClick={() => deleteOtherCharge(_item, index)}>
									<Delete />
								</Button>
							</ListItemWrapper>
						)
					})}
				</ListWrapper>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				{!edit ? (
					<Button size='base' onClick={submitHandler}>
						{strings('submit')}
					</Button>
				) : (
					<Button size='base' onClick={updateHandler}>
						{strings('update')}
					</Button>
				)}
			</DrawerFooterWrapper>
		</>
	)
}

export default AddOtherCharges
